import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    IconButton,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    FormControlLabel,
    Chip,
    CircularProgress,
    Alert,
    Avatar,
    Divider,
    Tooltip,
    Menu,
    MenuItem
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    MoreVert as MoreVertIcon,
    Lock as LockIcon,
    LockOpen as LockOpenIcon,
    Note as NoteIcon
} from '@mui/icons-material';
import authService from '../services/auth.service';
import { format } from 'date-fns';

interface Note {
    _id: string;
    userId:
        | {
              _id: string;
              fullName: string;
              email: string;
          }
        | string;
    userName?: string;
    content: string;
    createdAt: string;
    updatedAt: string;
    isPrivate: boolean;
}

interface DocumentNotesProps {
    documentId: string;
    open: boolean;
    onClose: () => void;
    onUpdate?: () => void;
}

const DocumentNotes: React.FC<DocumentNotesProps> = ({ documentId, open, onClose, onUpdate }) => {
    const [notes, setNotes] = useState<Note[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [newNote, setNewNote] = useState('');
    const [isPrivate, setIsPrivate] = useState(false);
    const [editingNote, setEditingNote] = useState<Note | null>(null);
    const [editContent, setEditContent] = useState('');
    const [editIsPrivate, setEditIsPrivate] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedNote, setSelectedNote] = useState<Note | null>(null);

    const currentUser = authService.getUser();

    useEffect(() => {
        if (open && documentId) {
            fetchNotes();
        }
    }, [open, documentId]);

    const fetchNotes = async () => {
        try {
            setLoading(true);
            setError(null);
            const api = authService.getApiInstance();
            const response = await api.get(`/documents/${documentId}/notes`);
            setNotes(response.data.notes);
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to load notes');
        } finally {
            setLoading(false);
        }
    };

    const handleAddNote = async () => {
        if (!newNote.trim()) return;

        try {
            const api = authService.getApiInstance();
            const response = await api.post(`/documents/${documentId}/notes`, {
                content: newNote,
                isPrivate
            });

            // Add the new note to the list
            setNotes([...notes, response.data.note]);
            setNewNote('');
            setIsPrivate(false);

            // Call onUpdate callback to refresh parent component
            if (onUpdate) {
                onUpdate();
            }
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to add note');
        }
    };

    const handleUpdateNote = async () => {
        if (!editingNote || !editContent.trim()) return;

        try {
            const api = authService.getApiInstance();
            const response = await api.put(`/documents/${documentId}/notes/${editingNote._id}`, {
                content: editContent,
                isPrivate: editIsPrivate
            });

            // Update the note in the list
            setNotes(notes.map((note) => (note._id === editingNote._id ? response.data.note : note)));
            setEditingNote(null);
            setEditContent('');
            setEditIsPrivate(false);

            // Call onUpdate callback to refresh parent component
            if (onUpdate) {
                onUpdate();
            }
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to update note');
        }
    };

    const handleDeleteNote = async (noteId: string) => {
        try {
            const api = authService.getApiInstance();
            await api.delete(`/documents/${documentId}/notes/${noteId}`);

            // Remove the note from the list
            setNotes(notes.filter((note) => note._id !== noteId));
            handleCloseMenu();

            // Call onUpdate callback to refresh parent component
            if (onUpdate) {
                onUpdate();
            }
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to delete note');
        }
    };

    const handleEditClick = (note: Note) => {
        setEditingNote(note);
        setEditContent(note.content);
        setEditIsPrivate(note.isPrivate);
        handleCloseMenu();
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLElement>, note: Note) => {
        setAnchorEl(event.currentTarget);
        setSelectedNote(note);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
        setSelectedNote(null);
    };

    const canEditNote = (note: Note) => {
        const userId = typeof note.userId === 'string' ? note.userId : note.userId._id;
        return userId === currentUser?._id || currentUser?.role === 'admin';
    };

    const getUserName = (note: Note) => {
        if (typeof note.userId === 'string') {
            return note.userName || 'Unknown User';
        }
        return `${note.userId.fullName}`;
    };

    const getUserInitials = (note: Note) => {
        const name = getUserName(note);
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { height: '80vh' }
            }}
        >
            <DialogTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <NoteIcon />
                    Document Notes
                </Box>
            </DialogTitle>
            <DialogContent dividers>
                {error && (
                    <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {/* Add new note */}
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Add a Note
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Type your note here..."
                        value={newNote}
                        onChange={(e) => setNewNote(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={isPrivate}
                                    onChange={(e) => setIsPrivate(e.target.checked)}
                                    size="small"
                                />
                            }
                            label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {isPrivate ? <LockIcon fontSize="small" /> : <LockOpenIcon fontSize="small" />}
                                    <Typography variant="body2">Private note (only visible to you)</Typography>
                                </Box>
                            }
                        />
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddNote}
                            disabled={!newNote.trim()}
                        >
                            Add Note
                        </Button>
                    </Box>
                </Paper>

                {/* Notes list */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : notes.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">No notes yet. Be the first to add a note!</Typography>
                    </Box>
                ) : (
                    <List>
                        {notes.map((note, index) => (
                            <React.Fragment key={note._id}>
                                {index > 0 && <Divider />}
                                <ListItem alignItems="flex-start" sx={{ py: 2 }}>
                                    <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>{getUserInitials(note)}</Avatar>
                                    <ListItemText
                                        primary={
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Typography variant="subtitle2">{getUserName(note)}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {format(new Date(note.createdAt), 'MMM d, yyyy h:mm a')}
                                                </Typography>
                                                {note.isPrivate && (
                                                    <Chip
                                                        icon={<LockIcon />}
                                                        label="Private"
                                                        size="small"
                                                        color="warning"
                                                    />
                                                )}
                                                {note.updatedAt !== note.createdAt && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        (edited)
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            editingNote?._id === note._id ? (
                                                <Box sx={{ mt: 1 }}>
                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        rows={3}
                                                        value={editContent}
                                                        onChange={(e) => setEditContent(e.target.value)}
                                                        sx={{ mb: 1 }}
                                                    />
                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={editIsPrivate}
                                                                    onChange={(e) => setEditIsPrivate(e.target.checked)}
                                                                    size="small"
                                                                />
                                                            }
                                                            label="Private"
                                                        />
                                                        <Button
                                                            size="small"
                                                            onClick={handleUpdateNote}
                                                            disabled={!editContent.trim()}
                                                        >
                                                            Save
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            onClick={() => {
                                                                setEditingNote(null);
                                                                setEditContent('');
                                                                setEditIsPrivate(false);
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="text.primary"
                                                    sx={{ whiteSpace: 'pre-wrap' }}
                                                >
                                                    {note.content}
                                                </Typography>
                                            )
                                        }
                                    />
                                    {canEditNote(note) && editingNote?._id !== note._id && (
                                        <ListItemSecondaryAction>
                                            <IconButton edge="end" onClick={(e) => handleMenuClick(e, note)}>
                                                <MoreVertIcon />
                                            </IconButton>
                                        </ListItemSecondaryAction>
                                    )}
                                </ListItem>
                            </React.Fragment>
                        ))}
                    </List>
                )}

                {/* Context menu */}
                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}>
                    <MenuItem onClick={() => selectedNote && handleEditClick(selectedNote)}>
                        <EditIcon fontSize="small" sx={{ mr: 1 }} />
                        Edit
                    </MenuItem>
                    <MenuItem
                        onClick={() => selectedNote && handleDeleteNote(selectedNote._id)}
                        sx={{ color: 'error.main' }}
                    >
                        <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                        Delete
                    </MenuItem>
                </Menu>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default DocumentNotes;
